import { Chip } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import type { BlockerOut, CaseCloseoutOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { caseCloseoutQuery } from './api'

/**
 * What blocks close, as chips (#121), from `GET /api/v1/closeout/cases/{id}`. The case header renders
 * `<CaseBlockerChips caseId=… />` in the slot the header exposes; a queue row can render `<BlockerChips data=… />` from
 * the batched `GET /api/v1/closeout?case_id=…` answer.
 *
 * Loading renders nothing; a failed read says "close-out unknown" (never "nothing blocks"); a case whose rules could
 * not all be checked says so rather than looking clear.
 */

function when(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : formatWhen(d.toISOString(), { style: 'absolute' })
}

export function blockerTitle(b: BlockerOut): string {
  const parts = [b.label]
  if (b.owed_by_display) parts.push(`owed by ${b.owed_by_display}`)
  parts.push(b.overdue ? `overdue since ${when(b.due_at)}` : `chasing from ${when(b.due_at)}`)
  if (b.reminders_sent) parts.push(`${b.reminders_sent} reminder${b.reminders_sent === 1 ? '' : 's'} sent`)
  return parts.join(' · ')
}

/** One chip per blocker kind (#228): two drafts, or two work orders owing the same thing, read as one chip, overdue
 * (red) when any of them is, never a red and an orange copy of the same blocker side by side. */
export interface BlockerGroup {
  key: string
  first: BlockerOut
  items: BlockerOut[]
  overdue: boolean
  reminders: number
}

export function groupBlockers(blockers: BlockerOut[]): BlockerGroup[] {
  const by = new Map<string, BlockerGroup>()
  for (const b of blockers) {
    const k = `${b.rule_id}\u0000${b.label}`
    const g = by.get(k)
    if (!g) {
      by.set(k, { key: b.key, first: b, items: [b], overdue: b.overdue, reminders: b.reminders_sent ?? 0 })
      continue
    }
    g.items.push(b)
    g.overdue = g.overdue || b.overdue
    g.reminders = Math.max(g.reminders, b.reminders_sent ?? 0)
    if (b.overdue && !g.first.overdue) g.first = b
  }
  // Most severe first (#263): overdue groups, then the larger groups; equal ones keep the server's order.
  return [...by.values()]
    .map((g, i) => ({ g, i }))
    .sort((a, b) => Number(b.g.overdue) - Number(a.g.overdue) || b.g.items.length - a.g.items.length || a.i - b.i)
    .map(({ g }) => g)
}

function groupTitle(g: BlockerGroup): string {
  if (g.items.length === 1) return blockerTitle(g.first)
  return [`${g.first.label} (${g.items.length})`, ...g.items.map(blockerTitle)].join('\n')
}

/** #271: the blocker a person can clear right here, on an already-closed case. */
export const NO_TIME_RULE = 'tech.no_time_logged'
export const BILLING_RULE = 'billing.not_posted'

/** `labelled` (the case header's strip, #263) leads with one "Blocks close" label instead of a lock on every chip. */
export function BlockerChips({
  data,
  max = 4,
  labelled = false,
  onLogTime,
  onNotBillable,
}: {
  data: CaseCloseoutOut
  max?: number
  labelled?: boolean
  /** Given: "Closed with no time logged" becomes a button that opens the time prompt (log time, or No time + reason). */
  onLogTime?: () => void
  /** Billing/manager inline decision; the dialog records a reason before the write. */
  onNotBillable?: () => void
}) {
  const groups = groupBlockers(data.blockers)
  const shown = groups.slice(0, max)
  const more = groups.length - shown.length
  const unchecked = data.unevaluated?.length ?? 0
  if (!shown.length && !unchecked) return null
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="closeout-chips">
      {labelled ? (
        <span className="inline-flex h-5 items-center gap-1 text-xs text-muted-foreground">
          <Lock aria-hidden className="size-3.5 text-subtle" />
          Blocks close
        </span>
      ) : null}
      {shown.map((g) => {
        const fixTime = onLogTime && g.first.rule_id === NO_TIME_RULE
        const fixBilling = onNotBillable && g.first.rule_id === BILLING_RULE
        const receiveIds = [
          ...new Set(
            g.items.flatMap((item) => {
              if (item.rule_id !== 'waiting_on_parts') return []
              const id = item.evidence.hardware_order_id
              return item.evidence.delivered === true && typeof id === 'string' && id ? [id] : []
            }),
          ),
        ]
        const targets = g.items.map((item) => `blocker:${item.key}`).join(' ')
        return (
          <span
            key={g.key}
            className="inline-flex min-w-0 flex-wrap items-center gap-1"
            data-closeout-resolution={fixTime ? 'no-time' : fixBilling ? 'billing' : undefined}
          >
            <Chip tone={g.overdue ? 'danger' : 'warning'} title={groupTitle(g)} data-notification-targets={targets}>
              {labelled ? null : <Lock />}
              {g.first.label}
              {g.items.length > 1 ? <span className="tabular font-normal">({g.items.length})</span> : null}
              {g.reminders ? <span className="tabular font-normal">×{g.reminders}</span> : null}
            </Chip>
            {fixTime ? (
              <button
                type="button"
                className="cursor-pointer rounded px-1 text-xs font-medium text-primary underline underline-offset-2"
                aria-label="Mark no time, or log time, to resolve the close blocker"
                onClick={onLogTime}
              >
                Mark no time
              </button>
            ) : null}
            {fixBilling ? (
              <>
                <button
                  type="button"
                  className="cursor-pointer rounded px-1 text-xs font-medium text-primary underline underline-offset-2"
                  onClick={onNotBillable}
                >
                  Not billable: reason
                </button>
                <a href="/billing" className="rounded px-1 text-xs font-medium text-primary underline underline-offset-2">
                  Post to Books
                </a>
              </>
            ) : null}
            {receiveIds.map((id) => (
              <a
                key={id}
                href={`/receive/${id}`}
                className="rounded px-1 text-xs font-medium text-primary underline underline-offset-2"
                aria-label="Receive parts"
              >
                Receive
              </a>
            ))}
          </span>
        )
      })}
      {more > 0 ? <Chip tone="neutral">+{more} more</Chip> : null}
      {unchecked > 0 ? (
        <Chip tone="outline" title={(data.unevaluated ?? []).map((u) => `${u.rule_id}: ${u.reason}`).join('\n')}>
          {unchecked} close-out check{unchecked === 1 ? '' : 's'} unknown
        </Chip>
      ) : null}
    </span>
  )
}

/** The title row's one chip (#263): "3 block close", red when any is overdue; the detail strip lists them. */
export function BlockerSummaryChip({ data }: { data: CaseCloseoutOut }) {
  const groups = groupBlockers(data.blockers)
  const unchecked = data.unevaluated?.length ?? 0
  if (!groups.length && !unchecked) return null
  const overdue = groups.some((g) => g.overdue)
  const title = [
    ...groups.map(groupTitle),
    ...(data.unevaluated ?? []).map((u) => `${u.rule_id}: could not be checked (${u.reason})`),
  ].join('\n')
  return (
    <Chip tone={groups.length ? (overdue ? 'danger' : 'warning') : 'outline'} title={title} data-testid="closeout-summary">
      <Lock />
      {groups.length
        ? `${groups.length} block${groups.length === 1 ? 's' : ''} close`
        : `${unchecked} check${unchecked === 1 ? '' : 's'} unknown`}
    </Chip>
  )
}

export function CaseBlockerChips({
  caseId,
  max,
  labelled,
  compact,
  onLogTime,
  onNotBillable,
}: {
  caseId: string
  max?: number
  labelled?: boolean
  /** #271: the no-time chip opens the time prompt */
  onLogTime?: () => void
  /** #353: opens the required reason dialog for the billing blocker. */
  onNotBillable?: () => void
  /** One summary chip instead of the grouped chips (the case header's title row). */
  compact?: boolean
}) {
  const q = useQuery(caseCloseoutQuery(caseId))
  if (q.isPending) return null
  if (q.isError || !q.data) {
    // The header's detail strip (labelled) stays quiet: the title row's summary chip already says "unknown".
    if (labelled) return null
    return (
      <Chip tone="outline" title="The close-out check could not be read">
        Close-out unknown
      </Chip>
    )
  }
  if (compact) return <BlockerSummaryChip data={q.data} />
  return <BlockerChips data={q.data} max={max} labelled={labelled} onLogTime={onLogTime} onNotBillable={onNotBillable} />
}
